import { AfterViewInit, Component, EventEmitter, OnDestroy, OnInit, Output, TrackByFunction, ViewChild } from '@angular/core';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort, MatSortHeader } from '@angular/material/sort';
import {
  MatTableDataSource,
  MatTable,
  MatColumnDef,
  MatHeaderCellDef,
  MatHeaderCell,
  MatCellDef,
  MatCell,
  MatHeaderRowDef,
  MatHeaderRow,
  MatRowDef,
  MatRow,
} from '@angular/material/table';
import { Store } from '@ngrx/store';
import { combineLatest, Subscription } from 'rxjs';
import { selectHasPolicy } from 'src/app/authentication/store/user.selector';
import { createTextTranslation } from 'src/app/shared/localization';
import { DialogService } from 'src/app/shared/service/dialog.service';
import { FabButton, FabService } from 'src/app/shared/service/fab.service';
import { BatchTypologyEditDialogComponent } from '../batch-typology-edit-dialog/batch-typology-edit-dialog.component';
import { BuildingSummary } from '../model/building.model';
import { UpdateTypologyDto } from '../model/typology.dto';
import { DOWNLOAD_BUILDINGS_LIST_SIZE } from '../service/building.service';
import { updateNextControlDates, updateStatus, updateTypologies } from '../store/building.action';
import { downloadBuildingList } from '../store/buildings.action';
import { selectSelectedBuildings } from '../store/buildings.selector';
import { BatchNextControlDateEditDialogComponent } from '../batch-next-control-date-edit-dialog/batch-next-control-date-edit-dialog.component';
import { BatchStatusEditDialogComponent } from '../batch-status-edit-dialog/batch-status-edit-dialog.component';
import { UpdateStatusDto } from '../model/status.dto';
import { MatIcon } from '@angular/material/icon';
import { TranslocoDirective, TranslocoPipe } from '@jsverse/transloco';
import { MatCheckbox } from '@angular/material/checkbox';
import { TypologyIndicatorComponent } from '../typology-indicator/typology-indicator.component';
import { DatePipe } from '@angular/common';

interface RemovableBuilding extends BuildingSummary {
  removed: boolean;
}

@Component({
  selector: 'sibat-detailed-list',
  templateUrl: './detailed-list.component.html',
  styleUrls: ['./detailed-list.component.scss'],
  imports: [
    MatIcon,
    TranslocoDirective,
    MatTable,
    MatSort,
    MatColumnDef,
    MatHeaderCellDef,
    MatHeaderCell,
    MatCheckbox,
    MatCellDef,
    MatCell,
    MatSortHeader,
    TypologyIndicatorComponent,
    MatHeaderRowDef,
    MatHeaderRow,
    MatRowDef,
    MatRow,
    MatPaginator,
    TranslocoPipe,
    DatePipe,
  ],
})
export class DetailedListComponent implements AfterViewInit, OnDestroy, OnInit {
  @Output() hide = new EventEmitter();
  @ViewChild(MatPaginator) paginator: MatPaginator;
  @ViewChild(MatSort) sort: MatSort;

  dataSource = new MatTableDataSource<RemovableBuilding>([]);
  displayedColumns = ['select', 'address', 'typology', 'assignment', 'lastControlDate', 'nextControlDate'];

  canEditControl$ = this.store.select(selectHasPolicy('fab.control.get'));
  canOnlyExport$ = this.store.select(selectHasPolicy('fab.exportonly.get'));
  canEditTypology$ = this.store.select(selectHasPolicy('fab.typology.get'));
  canEditStatus$ = this.store.select(selectHasPolicy('buildings.status.edit'));

  /**
   * True if at least one building is removed but False if all buildings are removed
   * This is important because we dont want to show the "intermediate" check-mark if all buildings are removed
   */
  someRemoved = false;
  noneRemoved = true;

  readonly editControlDateFab = new FabButton({
    label: createTextTranslation('building.control.editNextDate'),
    action: () => this.editNextControlDate(),
  });

  readonly editTypologyFab = new FabButton({
    label: createTextTranslation('building.typology.edit'),
    action: () => this.editTypology(),
  });

  readonly editStatusFab = new FabButton({
    label: createTextTranslation('building.status.edit'),
    action: () => this.editStatus(),
  });

  readonly exportSelectionFab = new FabButton({
    label: createTextTranslation('building.list.exportTheSelection'),
    action: () => this.exportSelection(),
    tooltip: createTextTranslation('building.list.downloadTooltip', { maxDownloadSize: DOWNLOAD_BUILDINGS_LIST_SIZE }),
    icon: 'cloud_download',
  });

  private readonly subscriptions = new Subscription();

  constructor(private store: Store, private fabService: FabService, private dialogService: DialogService) {}

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  ngOnInit() {
    this.subscriptions.add(
      combineLatest([this.canEditControl$, this.canOnlyExport$, this.canEditTypology$, this.canEditStatus$]).subscribe(
        ([canEdit, canOnlyExport, canEditTypology, canEditStatus]) => {
          const buttons = new Set<FabButton>();

          if (canEdit) {
            buttons.add(this.editControlDateFab);
            buttons.add(this.exportSelectionFab);
          }

          if (canEditTypology) {
            buttons.add(this.editTypologyFab);
          }

          if (canOnlyExport) {
            buttons.clear();
            buttons.add(this.exportSelectionFab);
          }

          if (canEditStatus) {
            buttons.add(this.editStatusFab);
          }

          this.fabService.setFabOptions({ buttons: Array.from(buttons) });
        }
      )
    );

    this.subscriptions.add(
      this.store.select(selectSelectedBuildings).subscribe(buildings => {
        this.dataSource.data = buildings.map(building =>
          this.remove(building, this.dataSource.data.find(b => b.id === building.id)?.removed ?? false)
        );
      })
    );
  }

  ngAfterViewInit(): void {
    this.dataSource.paginator = this.paginator;
    this.dataSource.sort = this.sort;
    this.dataSource.sortingDataAccessor = (building, property) => {
      switch (property) {
        case 'address':
          return building.addresses.join('');
        case 'assignment':
          return building.localizedAffectations.join('');
        case 'typology':
          return building.currentColor ?? '';
        case 'lastControlDate':
          return building.lastControlDate ?? 0;
        case 'nextControlDate':
          return building.nextControlDate ?? 0;
        default:
          return String(building[property]);
      }
    };
  }

  buildingId: TrackByFunction<RemovableBuilding> = (_, item) => item.id;

  removeAll(allChecked: boolean) {
    this.noneRemoved = allChecked;
    this.someRemoved = false;
    this.dataSource.data = this.dataSource.data.map(building => this.remove(building, !allChecked));
    this.updateFab();
  }

  removeOne(building: RemovableBuilding, checked: boolean) {
    this.dataSource.data = this.dataSource.data.map(b => (b.id === building.id ? this.remove(b, !checked) : b));
    const [someRemoved, allRemoved] = this.dataSource.data.reduce(
      ([some, none], b) => [some || b.removed, none && b.removed],
      [false, true]
    );

    this.someRemoved = someRemoved && !allRemoved;
    this.noneRemoved = !someRemoved;
    this.updateFab();
  }

  hideFabAndSelf = () => {
    this.fabService.clearFabOptions();
    this.hide.emit();
  };

  private remove(building: BuildingSummary, removed = true): RemovableBuilding {
    return { ...building, removed };
  }

  private async editNextControlDate() {
    const buildings = this.dataSource.data.filter(b => !b.removed);
    const count = buildings.length;
    const nextControlDate = await this.dialogService.openDialogComponentAsync<string | null>(BatchNextControlDateEditDialogComponent, {
      count,
    });

    if (nextControlDate || nextControlDate === null) {
      this.store.dispatch(
        updateNextControlDates({
          buildingIds: buildings.map(b => b.id),
          date: nextControlDate,
        })
      );
    }
  }

  private async editTypology() {
    const buildings = this.dataSource.data.filter(b => !b.removed);
    const count = buildings.length;
    const typology = await this.dialogService.openDialogComponentAsync<UpdateTypologyDto>(BatchTypologyEditDialogComponent, {
      count,
    });

    if (typology) {
      this.store.dispatch(
        updateTypologies({
          buildingIds: buildings.map(b => b.id),
          typology,
        })
      );
    }
  }

  private async editStatus() {
    const buildings = this.dataSource.data.filter(b => !b.removed);
    const count = buildings.length;
    const statusChanged = await this.dialogService.openDialogComponentAsync<UpdateStatusDto>(BatchStatusEditDialogComponent, {
      buildings,
      count,
    });

    if (statusChanged) {
      this.store.dispatch(
        updateStatus({
          buildingIds: buildings.map(b => b.id),
          statusChanged,
        })
      );
    }
  }

  private updateFab() {
    const hasSelection = this.someRemoved || this.noneRemoved;
    this.editTypologyFab.disabled = !hasSelection;
    this.exportSelectionFab.disabled = !hasSelection;
    this.fabService.disableFab(!hasSelection);
  }

  private async exportSelection() {
    const selectedBuildingIds = this.dataSource.data.reduce((buildingIds, building) => {
      if (!building.removed) {
        buildingIds.push(building.id);
      }
      return buildingIds;
    }, [] as number[]);

    this.store.dispatch(downloadBuildingList({ buildingIds: selectedBuildingIds }));
  }
}
